import { offerCreditTopUp } from "../../lib/creditTopUp";
import { savedSatinJob, loadSatinJob } from "./satinJobStorage";
import { useEffect, useRef, useState } from "react";
import { createAutoSatinWorker } from "./createAutoSatinWorker";
import { getSubpaths, samplePath } from "../SvgEditor/helpers/splitPathToRails";
import { publishSatinJob } from "./satinJobEvents";
import { prepareCompletionSound, playCompletionSound } from "../../lib/completionSound";
import {
  contoursFromUnresolvedPieces,
  unfinishedSatinLabel,
} from "./satinUnresolvedRetry";
import "./AutoSatinBeta.css";
export default function AutoSatinBeta({
  jobContext = "unsaved",
  jobRevision,
  mmPerUnit,
  pathD,
  revision,
  retryRequest,
  onRetryRequestHandled,
  onPreview,
  onApply,
  onDiscard,
}) {
  const [state, setState] = useState({ phase: "idle" });
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (state.phase !== "working") return;
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [state.phase]);
  const worker = useRef(null);
  const timeout = useRef(null);
  const kickoff = useRef(null);
  const snapshot = JSON.stringify({
    pathD,
    mmPerUnit,
    revision: jobRevision === undefined ? revision : jobRevision,
  });
  const callbacks = useRef({ onPreview, onDiscard, onApply });
  callbacks.current = { onPreview, onDiscard, onApply };
  useEffect(() => {
    publishSatinJob({
      context: jobContext,
      phase: state.phase,
      startedAt: state.startedAt,
      message: state.message,
    });
  }, [jobContext, state.phase, state.startedAt, state.message]);
  useEffect(() => {
    setState({ phase: "idle" });
    callbacks.current.onDiscard();
    let active = true;
    loadSatinJob(jobContext)
      .then((saved) => {
        if (
          active &&
          !worker.current &&
          !kickoff.current &&
          saved &&
          !saved.consumed &&
          saved.snapshot === snapshot
        )
          kickoff.current = setTimeout(() => generate(true), 32);
      })
      .catch(() => {
        /* Starting a job reports storage errors before submission. */
      });
    return () => {
      active = false;
      clearTimeout(kickoff.current);
      kickoff.current = null;
      clearTimeout(timeout.current);
      worker.current?.terminate();
      worker.current = null;
      callbacks.current.onDiscard();
    };
    // Resume only the exact saved layer version.
  }, [snapshot, jobContext]);
  const leftoverPieces = jobRevision?.autoUnresolvedPieces || [];
  const leftoverCount = leftoverPieces.length;
  const generate = (resumeOrOptions = false) => {
    try {
      const resumeOnly =
        resumeOrOptions === true || resumeOrOptions?.resumeOnly === true;
      const saved = savedSatinJob(jobContext);
      const retrying = Boolean(
        resumeOrOptions?.retryUnresolved ||
          (resumeOnly && saved?.input?.retryUnresolved)
      );
      let contours;
      if (retrying && !(resumeOnly && saved?.input?.contours)) {
        contours = contoursFromUnresolvedPieces(leftoverPieces);
      } else if (!resumeOnly) {
        const paths = getSubpaths(pathD);
        if (!paths.length)
          throw new Error(
            "Auto could not read outlines from this layer. Select a path layer; grouped or transformed artwork needs to be flattened first."
          );
        if (paths.length > 128)
          throw new Error(
            `This layer has ${paths.length} contours. Auto supports up to 128 at a time; split the design into smaller layers.`
          );
        contours = paths.map((p) => {
          const points = samplePath(p, 600);
          const first = points[0];
          const last = points[points.length - 1];
          const extent = Math.max(
            ...points.map((q) => Math.hypot(q.x - first.x, q.y - first.y))
          );
          // Imported outlines can explicitly return to their start without Z.
          // Accept those, but do not silently bridge genuinely open paths.
          if (
            !/z\s*$/i.test(p) &&
            Math.hypot(first.x - last.x, first.y - last.y) >
              Math.max(1e-6, extent * 1e-5)
          )
            throw new Error(
              "Auto needs closed outlines. This layer contains an open path; close it before trying again."
            );
          return points.slice(0, -1);
        });
      } else {
        contours = saved?.input?.contours || [];
      }
      const next = createAutoSatinWorker();
      worker.current = next;
      const startedAt =
        saved?.snapshot === snapshot && !saved?.consumed
          ? saved.startedAt || Date.now()
          : Date.now();
      setState({ phase: "working", startedAt, retryUnresolved: retrying });
      const fail = (message, statusUncertain = false) => {
        if (worker.current !== next) return;
        clearTimeout(timeout.current);
        next.terminate();
        worker.current = null;
        offerCreditTopUp(message, "Satin");
        setState({ phase: "error", message, statusUncertain });
      };
      next.onerror = () =>
        fail(
          retrying
            ? "Auto could not finish the leftover piece. Your completed columns are unchanged."
            : "Auto could not finish this layer. Your existing rails are unchanged."
        );
      next.onmessage = ({ data }) => {
        if (worker.current !== next) return;
        if (data.progress) {
          setState((previous) => ({
            ...previous,
            phase: "working",
            message: data.progress,
            referenceImage: data.referenceImage || previous.referenceImage,
          }));
          return;
        }
        if (data.error) {
          fail(data.error, data.statusUncertain);
          return;
        }
        clearTimeout(timeout.current);
        next.terminate();
        worker.current = null;
        if (!data.result?.pairs?.length) {
          setState({
            phase: "error",
            message: retrying
              ? `Trace could not finish the leftover. ${data.result?.issues?.[0]?.action || "Draw satin edges for that piece by hand."} Completed columns were kept.`
              : `Trace could not validate any satin columns. ${data.result?.issues?.[0]?.action || "Add junction cuts or select the satin edges manually."} No rails were replaced.`,
          });
          return;
        }
        try {
          callbacks.current.onApply(data.result, {
            pathD,
            revision,
            snapshot,
            retryUnresolved: retrying,
          });
          setState({ phase: "applied", result: data.result, retryUnresolved: retrying });
          void playCompletionSound(`satin:${jobContext}:${startedAt}`);
          return true;
        } catch (error) {
          setState({ phase: "error", message: error.message });
        }
      };
      next.postMessage({
        contours,
        mmPerUnit,
        context: jobContext,
        snapshot,
        resumeOnly,
        startedAt,
        processingMode: "standard",
        retryUnresolved: retrying,
      });
    } catch (error) {
      setState({ phase: "error", message: error.message });
    }
  };
  const startJob = (retryUnresolved = false) => {
    prepareCompletionSound();
    onDiscard();
    setState({ phase: "working", startedAt: Date.now(), retryUnresolved });
    // Let the loading indicator paint before sampling SVG paths on the main thread.
    kickoff.current = setTimeout(
      () => generate(retryUnresolved ? { retryUnresolved: true } : false),
      32
    );
  };
  const run = () => startJob(false);
  const runLeftover = () => startJob(true);
  useEffect(() => {
    if (retryRequest == null) return;
    if (!leftoverCount || state.phase === "working") {
      onRetryRequestHandled?.();
      return;
    }
    runLeftover();
    onRetryRequestHandled?.();
    // One-shot from the partial-result banner; leftovers are read at click time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retryRequest]);
  const elapsed = Math.max(
    0,
    Math.floor((now - (state.startedAt || now)) / 1000)
  );
  const elapsedLabel = `${Math.floor(elapsed / 60)}:${String(elapsed % 60).padStart(2, "0")}`;
  const checkBack = new Date(
    (state.startedAt || now) + 10 * 60 * 1000
  ).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  const progress = state.message || "";
  const stage = /reconnect/i.test(progress)
    ? "Reconnecting to your progress…"
    : /queued/i.test(progress)
      ? "Your layer is in the queue"
      : /reference/i.test(progress)
        ? "Creating your embroidery reference…"
        : /revis/i.test(progress)
          ? "Refining the stitch layout…"
          : /planning/i.test(progress)
            ? "Planning your cut lines…"
            : /checking|snapping|building/i.test(progress)
              ? "Preparing and checking your satin columns…"
              : "Getting your layer ready…";
  return (
    <section
      className={`satin-ai-card ${state.phase === "idle" ? "satin-ai-card-idle" : ""}`}
      aria-label="Satin AI Beta"
      aria-busy={state.phase === "working"}
    >
      <button
        type="button"
        className="satin-ai-action satin-ai-action-single"
        data-tutorial="satin-auto-beta"
        onClick={state.statusUncertain ? () => generate(true) : leftoverCount ? runLeftover : run}
        disabled={state.phase === "working" || state.phase === "preview"}
      >
        <span className="satin-ai-action-icon" aria-hidden="true">
          ✦
        </span>
        <span className="satin-ai-action-copy">
          <span className="satin-ai-action-title">
            {state.phase === "working"
              ? state.retryUnresolved
                ? "Trace is finishing the leftover"
                : "Trace is working"
              : state.statusUncertain
                ? "Check job status"
                : leftoverCount
                ? `Retry ${unfinishedSatinLabel(leftoverCount)}`
                : "Let Trace handle it"}
          </span>
          <span className="satin-ai-action-detail">
            {leftoverCount
              ? "Keep finished columns. Send only the leftover area."
              : "Create cut lines and satin columns automatically"}
          </span>
        </span>
        <span className="satin-ai-action-end">
          <span className="satin-ai-action-beta">Beta</span>
          <span className="satin-ai-action-arrow" aria-hidden="true">
            →
          </span>
        </span>
      </button>
      {leftoverCount > 0 && state.phase !== "working" && (
        <button type="button" className="satin-ai-redo-layer" onClick={run}>
          Redo the whole layer
        </button>
      )}
      {state.phase === "working" && (
        <div className="auto-satin-loading">
          <div
            role="status"
            aria-live="polite"
            className="auto-satin-loading-status"
          >
            <span className="auto-satin-spinner" aria-hidden="true" />
            <div>
              <strong>{stage}</strong>
              <span className="auto-satin-loading-detail">
                {elapsed >= 600
                  ? state.retryUnresolved
                    ? "This leftover is taking longer than estimated. No completion time is available."
                    : "This layer is taking longer than estimated. No completion time is available."
                  : state.retryUnresolved
                    ? "This is only the leftover area, so it is usually faster than the first run."
                    : "Check back in 10 minutes. Detailed jobs may take longer, plus time in the queue."}
              </span>
            </div>
          </div>
          <div className="auto-satin-timing">
            <div>
              <span>Time elapsed</span>
              <strong role="timer" aria-live="off">
                {elapsedLabel}
              </strong>
            </div>
            <div>
              <span>{elapsed >= 600 ? "Check back" : "Check back around"}</span>
              <strong>{elapsed >= 600 ? "Time unknown" : checkBack}</strong>
            </div>
          </div>
          <p className="auto-satin-away">
            {state.retryUnresolved
              ? "You can switch tabs or leave this page. The leftover keeps processing. Jobs cannot be cancelled once started. Return to this design to see the result."
              : "You can switch tabs or leave this page. Your layer keeps processing. Jobs cannot be cancelled once started. Return to this design to see the result."}
          </p>
        </div>
      )}
      {state.phase === "error" && (
        <p role="alert" style={{ fontSize: 12 }}>
          {state.message}
        </p>
      )}
      {state.phase === "applied" && (
        state.result.complete ?
          <p role="status" style={{ fontSize: 12 }}>Trace finished: {state.result.pairs.length} satin columns applied. You can undo this change.</p> :
          <div role="alert" style={{ fontSize: 12 }}>
            <strong>Trace applied {state.result.pairs.length} validated satin columns.</strong>
            <p>{state.result.unresolved} area{state.result.unresolved === 1 ? "" : "s"} still need work. Completed columns were kept.</p>
            <ul>{(state.result.issues || []).map((issue, index) =>
              <li key={`${issue.component}-${issue.section ?? "whole"}-${index}`}>
                {issue.sectionId ? `Section ${issue.sectionId}` : `Component ${Number(issue.component) + 1}${issue.section == null ? "" : `, section ${Number(issue.section) + 1}`}`}: {issue.message} {issue.action}
              </li>)}</ul>
            {leftoverCount > 0 ? (
              <p>
                <button type="button" className="satin-ai-retry-leftover" onClick={runLeftover}>
                  Retry {unfinishedSatinLabel(leftoverCount)}
                </button>
              </p>
            ) : (
              <p>You can correct those areas manually or undo all applied columns.</p>
            )}
          </div>
      )}
    </section>
  );
}
